import React, { useState } from "react";
import { MapPin, Mail, Phone, Linkedin, Loader2, Check } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { organisationSchema, SITE_URL } from "../lib/seo";
import { gaEvent, CONVERSION_EVENTS } from "../lib/analytics";

export default function ContactPage() {
    useSEO({
        title: "Contact Optimum Hospitality Solutions Ltd | London Hospitality Consultancy",
        description:
            "Contact Optimum Hospitality Solutions Ltd. Registered office: Suite RA01, 195–197 Wood Street, London E17 3NU. Company Registration Number 17286143. Enquiries via website contact form, consultation booking, email or telephone.",
        path: "/contact",
    });
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.subject || !form.message) {
            toast.error("Please complete all fields.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contacts", form);
            gaEvent(CONVERSION_EVENTS.CONTACT_SUBMIT, { form_location: "contact_page" });
            setSubmitted(true);
            toast.success("Message received. We'll be in touch shortly.");
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const contactPageSchema = {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        url: `${SITE_URL}/contact`,
        name: "Contact — Optimum Hospitality Solutions Ltd",
        about: { "@id": `${SITE_URL}/#organization` },
    };

    return (
        <div data-testid="contact-page">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organisationSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(contactPageSchema) }} />
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                        Get in touch
                    </span>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">
                        Contact
                    </h1>
                    <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-2xl">
                        For new enquiries, partnerships or media, send a message below or use the details opposite.
                    </p>
                </div>
            </section>

            <section>
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <aside className="lg:col-span-5">
                        <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">
                            Registered Office
                        </div>
                        <h2 className="font-display text-2xl mt-3 text-brand-navy">
                            Optimum Hospitality Solutions Ltd
                        </h2>
                        <p className="mt-2 text-brand-ink/70 text-sm" data-testid="contact-company-number">
                            Company Registration Number: <span className="font-medium text-brand-navy">17286143</span>
                        </p>

                        <div className="mt-10 space-y-6">
                            <div className="flex items-start gap-4" data-testid="contact-detail-address">
                                <MapPin className="text-brand-champagne mt-1" size={20} strokeWidth={1.25} />
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-brand-ink/60">Registered Address</div>
                                    <address className="not-italic text-sm text-brand-ink mt-1 leading-relaxed">
                                        Suite RA01, 195–197 Wood Street<br/>
                                        London E17 3NU<br/>
                                        United Kingdom
                                    </address>
                                </div>
                            </div>
                            <div className="flex items-start gap-4" data-testid="contact-detail-email">
                                <Mail className="text-brand-champagne mt-1" size={20} strokeWidth={1.25} />
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-brand-ink/60">Email</div>
                                    <a
                                        href="mailto:info@optimumhospitalitysolutions.com"
                                        onClick={() => gaEvent(CONVERSION_EVENTS.EMAIL_CLICK, { location: "contact_page" })}
                                        className="text-sm text-brand-ink mt-1 inline-block link-underline"
                                    >
                                        info@optimumhospitalitysolutions.com
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4" data-testid="contact-detail-phone">
                                <Phone className="text-brand-champagne mt-1" size={20} strokeWidth={1.25} />
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-brand-ink/60">Telephone</div>
                                    <a
                                        href="tel:+442039962562"
                                        onClick={() => gaEvent(CONVERSION_EVENTS.PHONE_CLICK, { location: "contact_page" })}
                                        className="text-sm text-brand-ink mt-1 inline-block link-underline"
                                    >
                                        +44 20 3996 2562
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4" data-testid="contact-detail-linkedin">
                                <Linkedin className="text-brand-champagne mt-1" size={20} strokeWidth={1.25} />
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-brand-ink/60">LinkedIn</div>
                                    <a
                                        href="https://www.linkedin.com/in/sidney-a-a7ab35b7"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-brand-ink mt-1 inline-block link-underline"
                                    >
                                        Sidney Adams on LinkedIn
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="mt-12 border-t border-brand-border pt-8">
                            <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">
                                Response time
                            </div>
                            <p className="mt-3 text-sm text-brand-ink/75 leading-relaxed">
                                We aim to respond to all enquiries within one business day, Monday to Friday. All enquiries are handled through the website contact form, the consultation booking form, email or telephone.
                            </p>
                        </div>
                    </aside>

                    <div className="lg:col-span-7">
                        {submitted ? (
                            <div
                                data-testid="contact-success"
                                className="border border-brand-border bg-white p-12 text-center"
                            >
                                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-champagne/20 text-brand-champagne mx-auto">
                                    <Check size={28} strokeWidth={1.5} />
                                </div>
                                <h2 className="font-display text-3xl mt-6 text-brand-navy">
                                    Message received.
                                </h2>
                                <p className="mt-4 text-brand-ink/75 leading-relaxed">
                                    Thank you for reaching out. We&rsquo;ll respond within one business day.
                                </p>
                                <button
                                    onClick={() => {
                                        setSubmitted(false);
                                        setForm({ name: "", email: "", subject: "", message: "" });
                                    }}
                                    data-testid="contact-send-another"
                                    className="mt-8 inline-flex items-center px-6 py-3 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover transition-colors"
                                >
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={submit}
                                data-testid="contact-form"
                                className="border border-brand-border p-8 lg:p-12 bg-white space-y-7"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div>
                                        <Label htmlFor="c-name" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                            Full name
                                        </Label>
                                        <Input
                                            id="c-name"
                                            data-testid="contact-input-name"
                                            value={form.name}
                                            onChange={(e) => set("name", e.target.value)}
                                            className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="c-email" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                            Email
                                        </Label>
                                        <Input
                                            id="c-email"
                                            type="email"
                                            data-testid="contact-input-email"
                                            value={form.email}
                                            onChange={(e) => set("email", e.target.value)}
                                            className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="c-subject" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                        Subject
                                    </Label>
                                    <Input
                                        id="c-subject"
                                        data-testid="contact-input-subject"
                                        value={form.subject}
                                        onChange={(e) => set("subject", e.target.value)}
                                        className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="c-message" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                        Message
                                    </Label>
                                    <Textarea
                                        id="c-message"
                                        data-testid="contact-input-message"
                                        value={form.message}
                                        onChange={(e) => set("message", e.target.value)}
                                        rows={6}
                                        className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                        required
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    data-testid="contact-submit-button"
                                    disabled={submitting}
                                    className="rounded-none bg-brand-navy text-brand-stone hover:bg-brand-hover px-8 py-6 text-sm tracking-wide"
                                >
                                    {submitting ? (
                                        <span className="inline-flex items-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Sending...
                                        </span>
                                    ) : (
                                        "Send message"
                                    )}
                                </Button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
